import { useEffect, useLayoutEffect, useState } from 'react';
import Dagre from '@dagrejs/dagre';
import ReactFlow, {
  Controls,
  MarkerType,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import { useProcContext } from '@/entities/proc';
import { useGetLogicsByProcId } from '@/shared/api';
import 'reactflow/dist/style.css';

const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes: any, edges: any) => {
  g.setGraph({ rankdir: 'TB', nodesep: 50, edgesep: 200, ranksep: 80 });

  edges.forEach((edge: any) => g.setEdge(edge.source, edge.target));
  nodes.forEach((node: any) => g.setNode(node.id, node));

  Dagre.layout(g);

  return {
    nodes: nodes.map((node: any) => {
      const { x, y } = g.node(node.id);

      return { ...node, position: { x, y } };
    }),
    edges,
  };
};

export const EditProcLogicView = () => {
  const { proc, setSelectedStep } = useProcContext();
  const { fitView } = useReactFlow();

  const [localNodes, setLocalNodes] = useState<any>([]);
  const [localEdges, setLocalEdges] = useState<any>([]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const { data: logicsData } = useGetLogicsByProcId({
    pathParams: {
      procId: proc?.id!,
    },
  });

  const onLayout = () => {
    const layouted = getLayoutedElements(localNodes, localEdges);

    setNodes([...layouted.nodes]);
    setEdges([...layouted.edges]);

    window.requestAnimationFrame(() => {
      fitView({ maxZoom: 1.3 });
    });
  };

  const onNodeClick = (event: any, node: any) => {
    if (node.id === logicsData?.endStep) {
      return;
    }
    const stepIndex = proc?.stepData?.steps.findIndex(
      (step) => step.id === node.id,
    );
    setSelectedStep(proc?.stepData?.steps[stepIndex as number]);
  };

  useEffect(() => {
    const nodesData = proc?.stepData.steps.map((step, index) => {
      return {
        id: step.id,
        data: { label: step.title },
        position: { x: 0, y: (index + 1) * 100 },
        selected: false,
        selectable: false,
        connectable: false,
      };
    });
    setLocalNodes(nodesData as any);
  }, [proc?.stepData?.steps, logicsData?.endStep]);

  useEffect(() => {
    const data: any = [];
    logicsData?.stepLogics?.forEach((step: any, index: number) => {
      // defaultStep provided and equal to endStep
      if (logicsData.endStep && step.defaultGoToStep === logicsData.endStep) {
        if (step.logics.length === 0) {
          return;
        }
      }
      // no defaultStep provided, then connect to next step
      if (!step.defaultGoToStep) {
        data.push({
          id: `edge_${step.stepId}`,
          source: step.stepId,
          target: logicsData.stepLogics[index + 1]?.stepId,
          markerEnd: {
            type: MarkerType.Arrow,
          },
        });
      } else {
        // defaultStep provided and not equal to endStep, then connect to provided defaultStep
        if (step.defaultGoToStep !== logicsData.endStep) {
          data.push({
            id: `edge_${step.stepId}`,
            source: step.stepId,
            target: step.defaultGoToStep,
            markerEnd: {
              type: MarkerType.Arrow,
            },
          });
        }
      }
      // connect to step from logics

      if (step.logics.length !== 0) {
        step.logics.forEach((rule: any, index: number) => {
          if (rule.goToStep !== logicsData.endStep) {
            data.push({
              id: `edge_${step.stepId}${index}`,
              source: step.stepId,
              target: rule.goToStep,
              markerEnd: {
                type: MarkerType.Arrow,
              },
            });
          }
        });
      }
    });
    setLocalEdges(data);
  }, [logicsData]);

  useLayoutEffect(() => {
    if (localEdges.length && localNodes.length) {
      onLayout();
    }
  }, [localNodes, localEdges]);

  return (
    <ReactFlow
      className="logic-view"
      nodes={nodes}
      edges={edges}
      attributionPosition="top-right"
      onNodeClick={onNodeClick}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      fitView
      fitViewOptions={{ maxZoom: 1.3 }}
    >
      <Controls position="top-left" />
    </ReactFlow>
  );
};
